import { Popper } from '@mui/material';
import { isArray } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { tv } from 'tailwind-variants';

import { Button } from '../Button';
import { TextIconColor } from '../constants';
import { Icon, Icons } from '../Icon';

import { DropdownPlacement } from './Dropdown.consts';
import { DropdownTriggerButtonProps, isButton, isIcon } from './Dropdown.types';

// Define styles for components with complex variants directly in the component file
const dropdownTriggerStyles = tv({
  base: 'relative inline-block cursor-pointer rounded-md text-left',
  variants: {
    disabled: {
      true: 'cursor-not-allowed opacity-50'
    },
    highlightOnHover: {
      true: 'rounded-md p-1 hover:bg-white/10'
    }
  }
});

// The dropdown trigger can be one of:
// 1. An Icon - rendered as an Icons component
// 2. A DropdownTriggerButtonProps object - rendered as a Button component
// 3. A React.ReactNode - rendered directly
type DropdownTrigger = Icon | DropdownTriggerButtonProps | React.ReactNode;
interface DropdownProps {
  children: React.ReactNode;
  trigger: DropdownTrigger;
  closeOnClick?: boolean;
  'data-id'?: string | undefined;
  disabled?: boolean;
  highlightTriggerOnHover?: boolean;
  onClickOutside?: () => void;
  placement?: DropdownPlacement;
}

export const Dropdown: React.FC<DropdownProps> = ({
  trigger,
  children,
  'data-id': dataId,
  disabled = false,
  highlightTriggerOnHover = false,
  placement = DropdownPlacement.BOTTOM_START,
  closeOnClick = false,
  onClickOutside
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const popperRef = useRef<HTMLDivElement | null>(null);

  const renderTrigger = () => {
    if (isIcon(trigger)) {
      return <Icons color={TextIconColor.PRIMARY} icon={trigger} />;
    }

    if (isButton(trigger)) {
      const { hideChevron, buttonProps } = trigger;
      const getEndIcon = () => {
        if (buttonProps.endIcon) return buttonProps.endIcon;
        return hideChevron ? undefined : Icon.CHEVRON_DOWN;
      };
      return (
        <Button
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...buttonProps}
          endIcon={getEndIcon()}
          invertEndIcon={buttonProps.endIcon ? false : !!anchorEl}
          onClick={() => {}}
        />
      );
    }

    return trigger;
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        containerRef.current &&
        popperRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        !popperRef.current.contains(event.target as Node)
      ) {
        setAnchorEl(null);
        onClickOutside?.();
      }
    },
    [onClickOutside]
  );

  const handleChildClick = (event: React.MouseEvent<HTMLElement>) => {
    // Don't close if clicking the container itself
    if (event.target === event.currentTarget) return;

    // Close if closeOnClick is true and we clicked a menuitem
    // This works with DropdownItem and SelectOption which both use role="menuitem"
    // to indicate they are clickable items that should trigger the dropdown to close
    const target = event.target as Element;
    if (closeOnClick && target.closest('[role="menuitem"]')) {
      setAnchorEl(null);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const open = Boolean(anchorEl);
  const disableTriggerButton =
    disabled || !children || (isArray(children) && !children.length);

  const triggerClasses = dropdownTriggerStyles({
    disabled: disableTriggerButton,
    highlightOnHover: highlightTriggerOnHover
  });

  return (
    <div ref={containerRef}>
      <button
        aria-expanded={open}
        className={triggerClasses}
        data-id={dataId}
        data-testid="dropdown-trigger"
        disabled={disableTriggerButton}
        id="dropdown-trigger"
        onClick={handleClick}
      >
        {renderTrigger()}
      </button>
      <Popper
        anchorEl={anchorEl}
        open={open}
        placement={placement}
        ref={popperRef}
        slotProps={{
          root: {
            className: 'z-[9999] font-sans-3 pb-[10px]'
          }
        }}
        style={{ position: 'fixed' }}
      >
        <div
          aria-labelledby="menu-button"
          aria-orientation="vertical"
          className="relative flex max-h-[200px] min-w-[180px] flex-col gap-1 overflow-y-auto rounded-[5px] border border-solid border-white/20 bg-black px-1 py-2 shadow-[0px_13px_26.5px_0px_rgba(0,0,0,0.54)] backdrop-blur-[5px]"
          data-testid="dropdown-menu"
          id="dropdown-container"
          onClick={handleChildClick}
          role="menu"
          style={{ position: 'relative', zIndex: 1 }}
        >
          {children}
        </div>
      </Popper>
    </div>
  );
};

export default Dropdown;
